<template>
    <div v-if="shouldShow" class="w-full md:max-w-(--breakpoint-md) md:mx-auto mb-6 px-2">
        <div class="bg-surface-raised border border-border rounded-lg p-4 shadow-xs relative">
            <BaseButton type="button" theme="ghost" size="sm" icon-only class="absolute top-2 right-2" :aria-label="t('components.teams.banner.dismiss')" @click="handleDismiss">
                <i class="bi bi-x text-xl" aria-hidden="true"></i>
            </BaseButton>

            <div class="flex items-start gap-4 pr-10">
                <div class="shrink-0">
                    <img src="/img/ms-teams-icon.png" height="40" width="43" class="rounded" :alt="t('components.teams.banner.logoAlt')" />
                </div>
                <div class="flex justify-between items-center grow">
                    <p class="text-ink">{{ t("components.teams.banner.continueIn") }}</p>
                    <a class="base-btn-secondary" :href="teamsChatUrl" target="_blank" @click="handleContinueInTeams">{{ t("components.teams.banner.open") }}</a>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computedAsync, useMediaQuery } from "@vueuse/core";
import { useFetch } from "~vue/api";
import BaseButton from "~vue/components/BaseButton.vue";
import { isInTeams } from "~vue/utils";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, onUnmounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

/* ChatDetail owns the announcement region, not this component: this is the v-else of a
   <Transition> that unmounts on chat errors and dismissed offers, so a region held here would be
   recreated already carrying its text, which is not announced. */
const emit = defineEmits(["visibilityChange"]);

const { orgHasMsTeams, msTeamsAppUrl, userMessageCount, coachingSessionId, isSuppressed } = defineProps({
    orgHasMsTeams: { type: Boolean, default: false },
    msTeamsAppUrl: { type: String, default: "" },
    userMessageCount: { type: Number, default: 0 },
    coachingSessionId: { type: String, required: true },
    isSuppressed: { type: Boolean, default: false },
});

const isDismissed = ref(false);
const inTeams = computedAsync(isInTeams, false);
const isDesktop = useMediaQuery("(min-width: 768px)");

const dismissPayload = ref(null);
const { execute: persistDismiss } = useFetch("/api/accounts/ui-interactions", { immediate: false }).post(dismissPayload).json();

const shouldShow = computed(() => {
    return (
        isDesktop.value &&
        orgHasMsTeams &&
        msTeamsAppUrl &&
        !inTeams.value && // Don't show if already in MS Teams (iframe or iOS webview)
        userMessageCount >= 2 && // Show after user sends 2 messages
        !isDismissed.value &&
        !isSuppressed
    );
});

const handleDismiss = async () => {
    isDismissed.value = true;
    logUserInteraction("ms_teams_chat_banner_dismissed", {
        coaching_session_id: coachingSessionId,
        user_message_count: userMessageCount,
    });
    dismissPayload.value = { interaction_type: "ms_teams_chat_banner_dismissed" };
    await persistDismiss();
    dismissPayload.value = null;
};

const handleContinueInTeams = () => {
    logUserInteraction("ms_teams_chat_banner_continue_clicked", {
        coaching_session_id: coachingSessionId,
        user_message_count: userMessageCount,
    });

    return true; // Ensures we follow the href on the link
};

const teamsChatUrl = computed(() => `/microsoft-teams/continue-chat/?cs_id=${coachingSessionId}`);

// track when banner becomes visible
const wasShown = ref(false);
watch(
    shouldShow,
    (newShouldShow) => {
        if (!newShouldShow || wasShown.value) {
            return;
        }

        wasShown.value = true;
        logUserInteraction("ms_teams_chat_banner_shown", {
            coaching_session_id: coachingSessionId,
            user_message_count: userMessageCount,
        });
    },
    { immediate: true },
);

watch(shouldShow, (visible) => emit("visibilityChange", visible), { immediate: true });

// The region has to empty while the banner is away, or the next appearance writes the same string
// back and a screen reader sees no change.
onUnmounted(() => emit("visibilityChange", false));
</script>
